/**
 * Finance Dashboard - Summary widgets and quick links
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  DollarSign, 
  CreditCard, 
  Users, 
  CheckSquare,
  TrendingUp,
  AlertTriangle,
  Clock,
  ArrowRight,
  Loader2,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue', onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
  };

  return (
    <Card 
      className={`hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
            <p className="text-2xl font-bold mt-0.5">{value}</p>
            {subValue && (
              <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>
            )}
          </div>
          {onClick && <ArrowRight className="h-4 w-4 text-slate-300 mt-2" />}
        </div>
      </CardContent>
    </Card>
  );
};

// Quick Link Card
const QuickLinkCard = ({ icon: Icon, label, description, onClick }) => (
  <Card 
    className="hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
    onClick={onClick}
  >
    <CardContent className="p-4 flex items-center gap-3">
      <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700">
        <Icon className="h-5 w-5 text-slate-600 dark:text-slate-300" />
      </div>
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
    </CardContent>
  </Card>
);

export default function FinanceDashboard({ user, onNavigate }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      const response = await axios.get(`${API}/finance/hub/summary`);
      setSummary(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard summary:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-10 w-10 mx-auto text-amber-500 mb-2" />
        <p className="text-slate-500">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => fetchSummary()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Financial Overview</h2>
          <p className="text-sm text-slate-500">
            Last updated: {summary?.last_updated ? new Date(summary.last_updated).toLocaleTimeString() : '—'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchSummary(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Widgets - Row 1: AR & Cash */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={TrendingUp}
          label="AR Invoiced"
          value={formatCurrency(summary?.ar_invoiced)}
          subValue={`${summary?.ar_invoice_count || 0} invoices`}
          color="blue"
          onClick={() => onNavigate('invoices')}
        />
        <StatCard
          icon={DollarSign}
          label="AR Collected"
          value={formatCurrency(summary?.ar_collected)}
          color="green"
          onClick={() => onNavigate('payments')}
        />
        <StatCard
          icon={AlertTriangle}
          label="AR Balance Due"
          value={formatCurrency(summary?.ar_balance)}
          subValue={summary?.ar_overdue_count > 0 ? `${summary.ar_overdue_count} overdue` : 'None overdue'}
          color={summary?.ar_balance > 0 ? 'amber' : 'green'}
          onClick={() => onNavigate('invoices')}
        />
        <StatCard
          icon={CreditCard}
          label="Cash + Undeposited"
          value={formatCurrency(summary?.total_cash)}
          subValue={summary?.undeposited_balance > 0 ? `${formatCurrency(summary.undeposited_balance)} undeposited` : ''}
          color="green"
        />
      </div>

      {/* Summary Widgets - Row 2: Commissions & Approvals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Commissions Pending"
          value={formatCurrency(summary?.commission_pending)}
          subValue={`${summary?.commission_batch_count || 0} batches`}
          color="purple"
          onClick={() => onNavigate('commissions')}
        />
        <StatCard
          icon={CheckSquare}
          label="Commissions Approved"
          value={formatCurrency(summary?.commission_approved)}
          subValue="Ready to pay"
          color="green"
          onClick={() => onNavigate('commissions')}
        />
        <StatCard
          icon={DollarSign}
          label="Paid YTD"
          value={formatCurrency(summary?.commission_paid_ytd)}
          color="blue"
        />
        <StatCard
          icon={Clock}
          label="Pending Approvals"
          value={summary?.approvals_pending || 0}
          subValue={Object.entries(summary?.approvals_by_type || {}).map(([k, v]) => `${v} ${k}`).join(', ') || 'None'}
          color={summary?.approvals_pending > 0 ? 'amber' : 'green'}
          onClick={() => onNavigate('approvals')}
        />
      </div>

      {/* Today's Queue Panel */}
      {summary?.approvals_pending > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-700 dark:text-amber-400">
                    {summary.approvals_pending} items awaiting approval
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-500">
                    {Object.entries(summary.approvals_by_type || {}).map(([type, count]) => (
                      `${count} ${type.replace('_', ' ')}${count > 1 ? 's' : ''}`
                    )).join(', ')}
                  </p>
                </div>
              </div>
              <Button onClick={() => onNavigate('approvals')}>
                Review Queue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Last 7 Days</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">New Invoices</span>
                <span className="font-medium">{summary?.recent_invoices || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Payments Received</span>
                <span className="font-medium">{summary?.recent_payments || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={() => onNavigate('invoices')}>
                View Invoices
              </Button>
              <Button variant="outline" size="sm" onClick={() => onNavigate('payments')}>
                View Payments
              </Button>
              <Button variant="outline" size="sm" onClick={() => onNavigate('commissions')}>
                Commission Batches
              </Button>
              <Button variant="outline" size="sm" onClick={() => onNavigate('accounts')}>
                Chart of Accounts
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
